import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ShieldCheck, ArrowLeft, Upload, FileText, ImageIcon,
  CheckCircle2, XCircle, Clock, AlertTriangle, Sparkles, RefreshCw,
  Lock, Unlock, History, Search, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  getCredential, listCredentialFiles, uploadCredentialFile,
  getCredentialFileUrl, updateCredentialStatus, statusTone, daysUntil,
  runOcrForFile, listCredentialOcrRuns, listCredentialOcrJobs,
  processOcrQueueNow, applyOcrField, setFieldLock, verifyDpsstIris,
  type Credential, type CredentialFile, type CredentialOcrRun, type CredentialOcrJob,
} from "@/lib/credentials";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/credentials/$credentialId")({
  component: CredentialDetail,
  head: () => ({ meta: [{ title: "Credential — MissionReady" }] }),
});

const FIELDS = [
  "holder_full_name", "credential_number", "issued_at", "expires_at",
  "issuing_authority", "jurisdiction", "holder_dob",
] as const;

function CredentialDetail() {
  const { credentialId } = Route.useParams();
  const navigate = useNavigate();
  const { user, currentRole } = useAuth();
  const isReviewer = ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");

  const [cred, setCred] = useState<Credential | null>(null);
  const [files, setFiles] = useState<CredentialFile[]>([]);
  const [runs, setRuns] = useState<CredentialOcrRun[]>([]);
  const [jobs, setJobs] = useState<CredentialOcrJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [c, fs, rs, js] = await Promise.all([
        getCredential(credentialId),
        listCredentialFiles(credentialId),
        listCredentialOcrRuns(credentialId).catch(() => []),
        listCredentialOcrJobs(credentialId).catch(() => []),
      ]);
      setCred(c); setFiles(fs); setRuns(rs); setJobs(js);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to load credential");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [credentialId]);

  useEffect(() => {
    const ch = supabase
      .channel(`credential:${credentialId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "credentials", filter: `id=eq.${credentialId}` }, () => refresh())
      .on("postgres_changes", { event: "*", schema: "public", table: "credential_files", filter: `credential_id=eq.${credentialId}` }, () => refresh())
      .on("postgres_changes", { event: "*", schema: "public", table: "credential_ocr_runs", filter: `credential_id=eq.${credentialId}` }, () => refresh())
      .on("postgres_changes", { event: "*", schema: "public", table: "credential_ocr_jobs", filter: `credential_id=eq.${credentialId}` }, () => refresh())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line
  }, [credentialId]);

  const onPickFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!list.length || !cred) return;
    setBusy(true);
    try {
      for (const f of list) {
        if (f.size > 25 * 1024 * 1024) { toast.error(`${f.name} exceeds 25MB`); continue; }
        await uploadCredentialFile({ credential: cred, file: f });
      }
      toast.success("Uploaded — OCR queued");
      refresh();
    } catch (err: any) { toast.error(err?.message ?? "Upload failed"); }
    finally { setBusy(false); }
  };

  const openFile = async (f: CredentialFile) => {
    try {
      const url = await getCredentialFileUrl(f.storage_path);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: any) { toast.error(e?.message ?? "Could not open file"); }
  };

  const verify = async () => {
    if (!cred) return;
    setBusy(true);
    try {
      await updateCredentialStatus(cred.id, { status: "verified", rejection_reason: null });
      await supabase.from("credential_verifications").insert({
        credential_id: cred.id, organization_id: cred.organization_id,
        attempted_by: user?.id, method: "manual", outcome: "match",
        message: "Manually verified by reviewer",
      });
      toast.success("Marked as verified"); refresh();
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!cred) return;
    if (!reason.trim()) return toast.error("Provide a rejection reason");
    setBusy(true);
    try {
      await updateCredentialStatus(cred.id, { status: "rejected", rejection_reason: reason.trim() });
      await supabase.from("credential_verifications").insert({
        credential_id: cred.id, organization_id: cred.organization_id,
        attempted_by: user?.id, method: "manual", outcome: "rejected",
        message: reason.trim(),
      });
      toast.success("Credential rejected"); setReason(""); refresh();
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  };

  const runQueue = async () => {
    setBusy(true);
    try {
      const r: any = await processOcrQueueNow(5);
      toast.success(`Processed ${r?.processed ?? 0} job(s)`);
      refresh();
    } catch (e: any) { toast.error(e?.message ?? "Queue failed"); }
    finally { setBusy(false); }
  };

  const apply = async (field: string, value: string, lock = false) => {
    if (!cred) return;
    try {
      await applyOcrField({ credential_id: cred.id, field, value, lock });
      toast.success(`Applied ${field}`);
      refresh();
    } catch (e: any) { toast.error(e?.message ?? "Apply failed"); }
  };

  const toggleLock = async (field: string, locked: boolean) => {
    if (!cred) return;
    try {
      await setFieldLock(cred.id, field, !locked);
      toast.success(!locked ? "Field locked" : "Field unlocked");
      refresh();
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };

  const runIris = async () => {
    if (!cred) return;
    setBusy(true);
    try {
      const r: any = await verifyDpsstIris(cred.id);
      toast.success(`IRIS lookup: ${r?.outcome ?? "done"}`);
      refresh();
    } catch (e: any) { toast.error(e?.message ?? "IRIS failed"); }
    finally { setBusy(false); }
  };

  if (loading || !cred) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const tone = statusTone(cred.status);
  const days = daysUntil(cred.expires_at);
  const isOwner = cred.user_id === user?.id;
  const locked = new Set(cred.reviewer_locked_fields ?? []);
  const ai = (cred.ai_parsed ?? {}) as Record<string, any>;
  const dpsstCode = cred.credential_types?.code ?? "";
  const showIris = ["dpsst_unarmed", "dpsst_armed"].includes(dpsstCode) && isReviewer;
  const queuedCount = jobs.filter((j) => j.status === "queued" || j.status === "running").length;

  // Build diff: AI extracted vs current credential value
  const diffs = FIELDS.map((k) => {
    const aiVal = ai[k];
    const curVal = (cred as any)[k];
    if (!aiVal) return null;
    if (String(aiVal) === String(curVal ?? "")) return null;
    return { field: k as string, ai: String(aiVal), current: curVal == null ? null : String(curVal), locked: locked.has(k) };
  }).filter(Boolean) as { field: string; ai: string; current: string | null; locked: boolean }[];

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 md:p-6">
      <button onClick={() => navigate({ to: "/credentials" })} className="text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="mr-1 inline h-4 w-4" /> Back
      </button>

      <header className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{cred.credential_types?.category}</div>
            <h1 className="font-display text-xl font-semibold">
              <ShieldCheck className="mr-2 inline h-5 w-5 text-primary" />
              {cred.credential_types?.name ?? "Credential"}
            </h1>
            <div className="mt-1 text-sm text-muted-foreground">
              {cred.credential_types?.issuing_authority || cred.issuing_authority || "—"}
              {cred.jurisdiction ? ` · ${cred.jurisdiction}` : ""}
            </div>
          </div>
          <span className={`shrink-0 rounded-full border px-2 py-1 text-xs font-medium ${tone.className}`}>{tone.label}</span>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <Field label="Holder" value={cred.holder_full_name ?? "—"} locked={locked.has("holder_full_name")} />
          <Field label="Number" value={cred.credential_number ?? "—"} locked={locked.has("credential_number")} />
          <Field label="Issued" value={cred.issued_at ? new Date(cred.issued_at).toLocaleDateString() : "—"} locked={locked.has("issued_at")} />
          <Field
            label="Expires"
            value={cred.expires_at ? `${new Date(cred.expires_at).toLocaleDateString()}${days !== null ? ` (${days >= 0 ? `${days}d` : "overdue"})` : ""}` : "—"}
            tone={days !== null && days < 0 ? "destructive" : days !== null && days <= 30 ? "warning" : undefined}
            locked={locked.has("expires_at")}
          />
        </dl>

        {cred.awaiting_hard_card && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              Holder is awaiting their physical hard card.
              {cred.hard_card_due_date && ` Expected by ${new Date(cred.hard_card_due_date).toLocaleDateString()}.`}
            </div>
          </div>
        )}

        {cred.rejection_reason && (
          <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <strong>Rejected:</strong> {cred.rejection_reason}
          </div>
        )}
      </header>

      {/* Documents */}
      <section className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">
            Documents <span className="ml-1 text-sm text-muted-foreground">{files.length}</span>
            {queuedCount > 0 && <span className="ml-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-300">{queuedCount} OCR queued</span>}
          </h2>
          <div className="flex items-center gap-2">
            {isReviewer && queuedCount > 0 && (
              <Button size="sm" variant="outline" onClick={runQueue} disabled={busy}>
                <PlayCircle className="h-4 w-4" /> Process queue
              </Button>
            )}
            {(isOwner || isReviewer) && (
              <>
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}>
                  <Upload className="h-4 w-4" /> {busy ? "…" : "Upload"}
                </Button>
                <input ref={fileRef} hidden multiple type="file" accept="image/*,application/pdf" onChange={onPickFiles} />
              </>
            )}
          </div>
        </div>
        {files.length === 0 ? (
          <div className="mt-3 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No documents uploaded.
          </div>
        ) : (
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {files.map((f) => {
              const isImg = (f.mime_type ?? "").startsWith("image/");
              const Icon = isImg ? ImageIcon : FileText;
              const tone =
                f.ocr_status === "completed" ? "text-emerald-400" :
                f.ocr_status === "failed" ? "text-destructive" :
                f.ocr_status === "needs_review" ? "text-amber-400" :
                f.ocr_status === "processing" ? "text-amber-400" : "";
              return (
                <li key={f.id} className="flex items-center gap-3 rounded-lg border border-border bg-background/40 p-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                  <button onClick={() => openFile(f)} className="min-w-0 flex-1 text-left">
                    <div className="truncate text-sm font-medium hover:underline">{f.file_kind}</div>
                    <div className="text-xs text-muted-foreground">
                      {f.size_bytes ? `${Math.round(f.size_bytes / 1024)} KB · ` : ""}
                      OCR: <span className={tone}>{f.ocr_status}</span>
                    </div>
                  </button>
                  {(isOwner || isReviewer) && f.ocr_status !== "processing" && (
                    <Button size="icon" variant="ghost" title="Re-run OCR"
                      onClick={async () => {
                        try { await runOcrForFile(f.id); toast.success("OCR completed"); refresh(); }
                        catch (e: any) { toast.error(e?.message ?? "OCR failed"); }
                      }}>
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Suggested AI changes diff */}
      {(diffs.length > 0 || Object.keys(ai).length > 0) && (
        <section className="rounded-xl border border-primary/30 bg-primary/5 p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">
              <Sparkles className="mr-2 inline h-4 w-4 text-primary" />
              AI suggestions
            </h2>
            {ai.confidence != null && (
              <span className="text-xs text-muted-foreground">Confidence: {Math.round((ai.confidence ?? 0) * 100)}%</span>
            )}
          </div>
          {diffs.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No outstanding suggestions — current values match the AI extraction.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {diffs.map((d) => (
                <li key={d.field} className="rounded-lg border border-border bg-background/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      {d.field.replace(/_/g, " ")} {d.locked && <Lock className="ml-1 inline h-3 w-3 text-amber-400" />}
                    </div>
                    {isReviewer && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" onClick={() => apply(d.field, d.ai, false)} disabled={d.locked}>Apply</Button>
                        <Button size="sm" variant="outline" onClick={() => apply(d.field, d.ai, true)} disabled={d.locked}>Apply &amp; lock</Button>
                        <Button size="icon" variant="ghost" title={d.locked ? "Unlock" : "Lock current"} onClick={() => toggleLock(d.field, d.locked)}>
                          {d.locked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div className="rounded border border-border bg-card p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">Current</div>
                      <div className="font-medium">{d.current ?? <span className="text-muted-foreground">empty</span>}</div>
                    </div>
                    <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-2">
                      <div className="text-[10px] uppercase text-emerald-400">AI extracted</div>
                      <div className="font-medium">{d.ai}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-[11px] text-muted-foreground">
            AI extraction is advisory. A reviewer must confirm before status becomes Verified.
          </p>
        </section>
      )}

      {/* OCR run history */}
      {runs.length > 0 && (
        <section className="rounded-xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-semibold">
            <History className="mr-2 inline h-4 w-4" /> OCR history
          </h2>
          <ul className="mt-3 divide-y divide-border">
            {runs.slice(0, 8).map((r) => (
              <li key={r.id} className="py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className={
                    r.status === "completed" ? "text-emerald-400" :
                    r.status === "needs_review" ? "text-amber-400" : "text-destructive"
                  }>
                    {r.status} {r.confidence != null && `· ${Math.round(r.confidence * 100)}%`}
                  </span>
                  <span className="text-muted-foreground">{new Date(r.completed_at).toLocaleString()}</span>
                </div>
                {r.error && <div className="mt-1 text-destructive">{r.error}</div>}
                {r.validation_issues?.length > 0 && (
                  <ul className="mt-1 list-disc pl-4 text-amber-400">
                    {r.validation_issues.slice(0, 3).map((i, idx) => (
                      <li key={idx}>{i.field}: {i.message}</li>
                    ))}
                  </ul>
                )}
                {r.applied_changes && Object.keys(r.applied_changes).length > 0 && (
                  <div className="mt-1 text-muted-foreground">
                    Applied: {Object.keys(r.applied_changes).join(", ")}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Reviewer actions */}
      {isReviewer && cred.status !== "verified" && (
        <section className="rounded-xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-semibold">Reviewer actions</h2>
          <p className="mt-1 text-xs text-muted-foreground">Manual review. State integrations appear below when configured.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button onClick={verify} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Verify
            </Button>
            {showIris && (
              <Button onClick={runIris} disabled={busy} variant="outline">
                <Search className="h-4 w-4" /> Run Oregon DPSST IRIS lookup
              </Button>
            )}
          </div>
          {cred.state_lookup_last_at && (
            <div className="mt-3 rounded-lg border border-border bg-background/40 p-3 text-xs">
              <div className="font-medium">Last state lookup</div>
              <div className="mt-1 text-muted-foreground">
                {new Date(cred.state_lookup_last_at).toLocaleString()} —{" "}
                {(cred.state_lookup_last_result as any)?.outcome ?? "unknown"} ·{" "}
                {(cred.state_lookup_last_result as any)?.message ?? ""}
              </div>
            </div>
          )}
          <div className="mt-4 space-y-2">
            <Label className="text-xs">Rejection reason</Label>
            <Textarea aria-label="Rejection reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Document is illegible, name does not match…" />
            <Button variant="destructive" onClick={reject} disabled={busy}>
              <XCircle className="h-4 w-4" /> Reject
            </Button>
          </div>
        </section>
      )}

      <div className="text-xs text-muted-foreground">
        <Clock className="mr-1 inline h-3 w-3" />
        Updated {new Date(cred.updated_at).toLocaleString()}
      </div>
    </div>
  );
}

function Field({ label, value, tone, locked }: { label: string; value: string; tone?: "warning" | "destructive"; locked?: boolean }) {
  const cls = tone === "destructive" ? "text-destructive" : tone === "warning" ? "text-amber-400" : "";
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label} {locked && <Lock className="ml-0.5 inline h-2.5 w-2.5 text-amber-400" />}
      </dt>
      <dd className={`mt-0.5 font-medium ${cls}`}>{value}</dd>
    </div>
  );
}
